import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trash2, Plus, X, Camera, ExternalLink, ShieldCheck, AlertTriangle, FileText, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const KINDS = [
  { id: 'raw_material', label: 'Raw Material' },
  { id: 'in_progress', label: 'WIP / In Progress' },
  { id: 'finished_unit', label: 'Finished Unit' },
];
const METHODS = ['Recycle', 'Landfill', 'Hazmat', 'Return-to-Vendor', 'Donate', 'Other'];
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-700',
  approved: 'bg-blue-100 text-blue-700',
  disposed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const DisposalTicketsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [filter, setFilter] = useState({ status: '', ticket_kind: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.ticket_kind) params.append('ticket_kind', filter.ticket_kind);
      const [tR, iR, uR] = await Promise.all([
        api().get(`/disposal-tickets?${params.toString()}`),
        api().get('/items'),
        api().get('/finished-units').catch(() => ({ data: [] })),
      ]);
      setTickets(tR.data || []);
      setItems(iR.data || []);
      setUnits(uR.data || []);
    } catch (e) { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filter.status, filter.ticket_kind]);

  const openNew = (kind = 'raw_material') => {
    setEditing({});
    setForm({
      ticket_kind: kind,
      item_id: null, item_sku: '', item_name: '', batch_lot: '', quantity: 1, base_uom: 'EA',
      unit_id: null, serial_number: '',
      reason: '', disposal_method: 'Recycle', notes: '', cost_impact: 0,
      photo_data: null,
    });
  };

  const close = () => { setEditing(null); setForm(null); };

  const setItemFromCatalog = (item_id) => {
    if (!item_id) { setForm(f => ({ ...f, item_id: null, item_sku: '', item_name: '', base_uom: 'EA' })); return; }
    const it = items.find(i => i.id === item_id);
    if (!it) return;
    setForm(f => ({ ...f, item_id: it.id, item_sku: it.item_sku, item_name: it.item_name, base_uom: it.base_uom || 'EA' }));
  };

  const setUnitFromCatalog = (unit_id) => {
    if (!unit_id) { setForm(f => ({ ...f, unit_id: null, serial_number: '' })); return; }
    const u = units.find(x => x.id === unit_id);
    if (!u) return;
    setForm(f => ({ ...f, unit_id: u.id, serial_number: u.serial_number }));
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Photo must be < 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, photo_data: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.reason?.trim()) { toast.error('Reason is required'); return; }
    if (form.ticket_kind === 'finished_unit' && !form.unit_id) { toast.error('Select a finished unit'); return; }
    if (form.ticket_kind !== 'finished_unit' && !form.item_id) { toast.error('Select an item'); return; }
    try {
      if (editing.id) {
        await api().put(`/disposal-tickets/${editing.id}`, form);
        toast.success('Ticket updated');
      } else {
        await api().post('/disposal-tickets', form);
        toast.success('Disposal ticket created');
      }
      close(); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const transition = async (t, to_status) => {
    if (to_status === 'disposed' && !window.confirm('Mark as disposed? This will deduct inventory (raw/WIP) or mark unit disposed (finished). Cannot be undone.')) return;
    try {
      await api().post(`/disposal-tickets/${t.id}/transition`, { to_status });
      toast.success(`Status: ${to_status}`);
      fetchAll();
      if (viewing?.id === t.id) {
        const r = await api().get(`/disposal-tickets/${t.id}`);
        setViewing(r.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Transition failed'); }
  };

  const deleteTicket = async (t) => {
    if (!window.confirm(`Delete ticket ${t.ticket_number}?`)) return;
    try {
      await api().delete(`/disposal-tickets/${t.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const view = (t) => setViewing(t);

  // iter67 — total product cost = quantity × unit cost (from items catalog) or fallback to cost_impact
  const totalProductCost = (t) => {
    if (!t) return 0;
    const it = items.find(i => i.id === t.item_id || i.item_sku === t.item_sku);
    const unitCost = it?.unit_cost ?? it?.last_cost ?? it?.average_cost ?? 0;
    const qty = parseFloat(t.quantity) || 0;
    const computed = unitCost * qty;
    if (computed > 0) return computed;
    return parseFloat(t.cost_impact) || 0;
  };

  // iter67 — Full-page printable disposal ticket (uses universal branded helper)
  const printFullTicket = async (t) => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    const cost = totalProductCost(t);
    const url = t.public_token ? `${window.location.origin}/public/disposal-tickets/${t.public_token}` : null;
    const left = `
      ${H.kv('Kind', H.escape(KINDS.find(k => k.id === t.ticket_kind)?.label || t.ticket_kind))}
      ${t.ticket_kind === 'finished_unit'
        ? H.kv('Serial Number', H.escape(t.serial_number || '—'), true)
        : `${H.kv('Item', `${H.escape(t.item_sku || '')} · ${H.escape(t.item_name || '')}`, true)}
           ${H.kv('Quantity', `${H.escape(t.quantity || '')} ${H.escape(t.base_uom || '')}`, true)}
           ${t.batch_lot ? H.kv('Lot', H.escape(t.batch_lot), true) : ''}`}
      ${H.kv('Disposal Method', H.escape(t.disposal_method || '—'))}
      ${H.kv('Reason', H.escape(t.reason || '—'))}
      ${t.notes ? H.kv('Notes', H.escape(t.notes)) : ''}`;
    const right = `
      ${H.kv('Reported By', `${H.escape(t.reported_by_name || '')}<br/><span style="font-size:9pt; color:#888;">${t.reported_at ? new Date(t.reported_at).toLocaleString() : ''}</span>`)}
      ${t.approved_at ? H.kv('Approved By', `${H.escape(t.approved_by_name || '')}<br/><span style="font-size:9pt; color:#888;">${new Date(t.approved_at).toLocaleString()}</span>`) : ''}
      ${t.disposed_at ? H.kv('Disposed By', `${H.escape(t.disposed_by_name || '')}<br/><span style="font-size:9pt; color:#888;">${new Date(t.disposed_at).toLocaleString()}</span>`) : ''}
      ${t.photo_data ? `<div class="label">Evidence Photo</div><img src="${t.photo_data}" style="max-width:100%; max-height:280px; border:1px solid #e5e7eb; border-radius:4px;" alt="evidence" />` : ''}`;
    const body = `
      <div class="grid-2">
        <div>${left}</div>
        <div>${right}</div>
      </div>
      ${H.totalBox('Total Product Cost', `$${cost.toFixed(2)}`)}
      <div style="font-size:8pt; color:#888; margin-top:4pt;">Quantity × current item cost (or recorded cost impact)</div>
      ${H.signoffLines('Authorized signature', 'Date')}`;
    const statusColor = t.status === 'disposed' ? '#16a34a' : t.status === 'approved' ? '#2563eb' : t.status === 'cancelled' ? '#dc2626' : '#6b7280';
    await openBrandedPrintWindow({
      title: 'DISPOSAL TICKET',
      subtitle: t.ticket_number,
      statusBadge: H.statusBadge(t.status, statusColor),
      body,
      qrUrl: url,
      footerNote: 'Internal Document · Disposal & Scrap Tracking',
    });
  };

  const printLabel = (t) => {
    const url = `${window.location.origin}/public/disposal-tickets/${t.public_token}`;
    const html = `<!doctype html><html><head><title>Label ${t.ticket_number}</title>
      <style>
        @page { size: 4in 6in; margin: 0; }
        body { font-family: 'Helvetica Neue', Arial; margin: 0; }
        .ticket-label { width: 4in; height: 6in; padding: 0.25in; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: space-between; border: 1px dashed #999; }
        .top { text-align: center; }
        .top h1 { font-size: 14pt; margin: 0 0 4pt 0; letter-spacing: 0.05em; }
        .top .num { font-size: 11pt; font-family: monospace; color: #555; }
        .qr-wrap { display: flex; justify-content: center; }
        .info { font-size: 9pt; line-height: 1.4; text-align: center; }
        .info .label { color: #666; text-transform: uppercase; font-size: 7pt; letter-spacing: 0.06em; }
        .footer { text-align: center; font-size: 7pt; color: #999; }
      </style></head><body>
      <div class="ticket-label">
        <div class="top">
          <h1>DISPOSAL TICKET</h1>
          <div class="num">${t.ticket_number}</div>
        </div>
        <div class="qr-wrap">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}" width="240" height="240" alt="QR" />
        </div>
        <div class="info">
          <div class="label">Kind</div>
          <div>${KINDS.find(k => k.id === t.ticket_kind)?.label}</div>
          ${t.ticket_kind === 'finished_unit' ? `<div class="label" style="margin-top:6pt;">Serial</div><div style="font-family:monospace;">${t.serial_number || ''}</div>` :
            `<div class="label" style="margin-top:6pt;">Item</div><div style="font-family:monospace;">${t.item_sku || ''}</div><div>${t.item_name || ''}</div><div class="label" style="margin-top:6pt;">Qty</div><div>${t.quantity || ''} ${t.base_uom || ''}</div>${t.batch_lot ? `<div class="label" style="margin-top:6pt;">Lot</div><div style="font-family:monospace;">${t.batch_lot}</div>` : ''}`}
          <div class="label" style="margin-top:6pt;">Method</div>
          <div>${t.disposal_method || ''}</div>
        </div>
        <div class="footer">Scan to verify · ${t.status?.toUpperCase()}</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) { toast.error('Pop-ups blocked'); return; }
    w.document.write(html); w.document.close();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const counts = {
    all: tickets.length,
    draft: tickets.filter(t => t.status === 'draft').length,
    approved: tickets.filter(t => t.status === 'approved').length,
    disposed: tickets.filter(t => t.status === 'disposed').length,
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="disposal-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Disposal Tickets</h1>
          <p className="text-slate-500 text-sm">Unified disposal repo — raw material, WIP, and finished units. Each ticket has a QR code for scan tracking.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openNew('finished_unit')} className="rounded-sm" data-testid="disposal-new-unit-btn"><Plus className="w-4 h-4 mr-1" /> Finished Unit</Button>
          <Button variant="outline" onClick={() => openNew('in_progress')} className="rounded-sm" data-testid="disposal-new-wip-btn"><Plus className="w-4 h-4 mr-1" /> WIP</Button>
          <Button onClick={() => openNew('raw_material')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="disposal-new-raw-btn"><Plus className="w-4 h-4 mr-1" /> Raw Material</Button>
        </div>
      </div>

      <Tabs value={filter.status || 'all'} onValueChange={(v) => setFilter({ ...filter, status: v === 'all' ? '' : v })}>
        <TabsList>
          <TabsTrigger value="all" data-testid="disposal-tab-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="draft" data-testid="disposal-tab-draft">Draft ({counts.draft})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="disposal-tab-approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="disposed" data-testid="disposal-tab-disposed">Disposed ({counts.disposed})</TabsTrigger>
        </TabsList>
        <TabsContent value={filter.status || 'all'} className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-0">
              {tickets.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No tickets</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket #</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>Item / Unit</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reported</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map(t => (
                      <TableRow key={t.id} data-testid={`disposal-row-${t.id}`}>
                        <TableCell><button onClick={() => view(t)} className="mono text-orange-600 hover:underline">{t.ticket_number}</button></TableCell>
                        <TableCell className="text-xs">{KINDS.find(k => k.id === t.ticket_kind)?.label || t.ticket_kind}</TableCell>
                        <TableCell className="text-sm">
                          {t.ticket_kind === 'finished_unit' ? <span className="mono">{t.serial_number || '—'}</span> : <><span className="mono">{t.item_sku}</span> · {t.item_name}</>}
                          {t.batch_lot && <span className="text-xs text-slate-500 ml-1">[lot {t.batch_lot}]</span>}
                        </TableCell>
                        <TableCell className="text-right mono text-sm">{t.quantity != null ? `${t.quantity} ${t.base_uom || ''}` : '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600">{t.disposal_method || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-500">{t.reported_at ? new Date(t.reported_at).toLocaleDateString() : '—'}<br /><span className="text-[10px]">{t.reported_by_name}</span></TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[t.status] || 'bg-slate-100'}`}>{t.status}</span></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {t.status === 'draft' && (
                              <Button size="sm" variant="ghost" onClick={() => transition(t, 'approved')} className="text-blue-600" data-testid={`disposal-approve-${t.id}`}>Approve</Button>
                            )}
                            {t.status === 'approved' && (
                              <Button size="sm" variant="ghost" onClick={() => transition(t, 'disposed')} className="text-emerald-700" data-testid={`disposal-dispose-${t.id}`}>Dispose</Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => view(t)} data-testid={`disposal-view-${t.id}`}><ExternalLink className="w-4 h-4" /></Button>
                            {(t.status === 'draft' || t.status === 'cancelled') && (
                              <Button size="sm" variant="ghost" onClick={() => deleteTicket(t)} className="text-red-600" data-testid={`disposal-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New / Edit Dialog */}
      {editing && form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="disposal-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">New Disposal Ticket — {KINDS.find(k => k.id === form.ticket_kind)?.label}</h2>
              <Button variant="ghost" size="sm" onClick={close}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label>Ticket Kind</Label>
                <select value={form.ticket_kind} onChange={(e) => setForm({ ...form, ticket_kind: e.target.value })} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="disposal-form-kind">
                  {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </div>

              {form.ticket_kind === 'finished_unit' ? (
                <div>
                  <Label>Finished Unit</Label>
                  <select value={form.unit_id || ''} onChange={(e) => setUnitFromCatalog(e.target.value || null)} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="disposal-form-unit">
                    <option value="">— select unit —</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.serial_number} ({u.product_type || u.unit_kind})</option>)}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <Label>Item</Label>
                    <select value={form.item_id || ''} onChange={(e) => setItemFromCatalog(e.target.value || null)} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="disposal-form-item">
                      <option value="">— select item —</option>
                      {items.map(it => <option key={it.id} value={it.id}>{it.item_sku} · {it.item_name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Quantity</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} className="rounded-sm mono" data-testid="disposal-form-qty" /></div>
                    <div><Label>UOM</Label><Input value={form.base_uom || ''} onChange={(e) => setForm({ ...form, base_uom: e.target.value })} className="rounded-sm mono" /></div>
                    <div><Label>Batch / Lot</Label><Input value={form.batch_lot || ''} onChange={(e) => setForm({ ...form, batch_lot: e.target.value })} placeholder="optional" className="rounded-sm mono" /></div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Disposal Method</Label>
                  <select value={form.disposal_method} onChange={(e) => setForm({ ...form, disposal_method: e.target.value })} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="disposal-form-method">
                    {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div><Label>Cost Impact ($)</Label><Input type="number" step="0.01" value={form.cost_impact || 0} onChange={(e) => setForm({ ...form, cost_impact: parseFloat(e.target.value) || 0 })} className="rounded-sm mono" /></div>
              </div>

              <div>
                <Label>Reason *</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Damaged during shipping, expired, scrap from rework, etc." className="rounded-sm" data-testid="disposal-form-reason" />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm" />
              </div>

              <div>
                <Label className="flex items-center gap-1"><Camera className="w-3 h-3" /> Photo Evidence (optional)</Label>
                <input type="file" accept="image/*" onChange={onPhoto} className="text-xs mt-1" data-testid="disposal-form-photo" />
                {form.photo_data && <img src={form.photo_data} alt="evidence" className="mt-2 max-h-40 border border-slate-200 rounded-sm" />}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="disposal-save-btn">Create Ticket</Button>
            </div>
          </div>
        </div>
      )}

      {/* View Dialog (with QR) */}
      {viewing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="disposal-view-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">{viewing.ticket_number}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[viewing.status] || 'bg-slate-100'}`}>{viewing.status}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setViewing(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">Kind</div>
                <div className="font-medium">{KINDS.find(k => k.id === viewing.ticket_kind)?.label}</div>
                <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Reason</div>
                <div className="text-sm">{viewing.reason || '—'}</div>
                <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Method</div>
                <div className="text-sm">{viewing.disposal_method || '—'}</div>
                {viewing.ticket_kind !== 'finished_unit' ? (
                  <>
                    <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Item</div>
                    <div className="text-sm mono">{viewing.item_sku} · {viewing.item_name}</div>
                    <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Quantity</div>
                    <div className="text-sm mono">{viewing.quantity} {viewing.base_uom || ''}{viewing.batch_lot ? ` · lot ${viewing.batch_lot}` : ''}</div>
                  </>
                ) : (
                  <>
                    <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Serial Number</div>
                    <div className="text-sm mono">{viewing.serial_number}</div>
                  </>
                )}
                <div className="text-xs uppercase text-slate-500 mt-3 mb-1">Reported</div>
                <div className="text-xs">{viewing.reported_by_name} · {new Date(viewing.reported_at).toLocaleString()}</div>
                {viewing.approved_at && <><div className="text-xs uppercase text-slate-500 mt-3 mb-1">Approved</div><div className="text-xs">{viewing.approved_by_name} · {new Date(viewing.approved_at).toLocaleString()}</div></>}
                {viewing.disposed_at && <><div className="text-xs uppercase text-slate-500 mt-3 mb-1">Disposed</div><div className="text-xs">{viewing.disposed_by_name} · {new Date(viewing.disposed_at).toLocaleString()}</div></>}
                {viewing.notes && <><div className="text-xs uppercase text-slate-500 mt-3 mb-1">Notes</div><div className="text-sm">{viewing.notes}</div></>}
                {/* iter67 — Total product cost */}
                <div className="mt-4 p-3 rounded border-2 border-red-200 bg-red-50">
                  <div className="text-xs uppercase text-red-700 mb-1">Total Product Cost</div>
                  <div className="text-2xl font-bold text-red-700 mono">${totalProductCost(viewing).toFixed(2)}</div>
                  <div className="text-[10px] text-red-600 mt-1">Quantity × current item cost</div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 border-l border-slate-100 pl-4">
                <div className="text-xs uppercase text-slate-500">Scan QR</div>
                {viewing.public_token && (
                  <QRCodeSVG value={`${window.location.origin}/public/disposal-tickets/${viewing.public_token}`} size={160} level="M" />
                )}
                <div className="text-[10px] text-slate-500 mono break-all text-center max-w-[160px]">{viewing.ticket_number}</div>
                {viewing.photo_data && (
                  <div className="mt-3 w-full">
                    <div className="text-xs uppercase text-slate-500 mb-1">Photo</div>
                    <img src={viewing.photo_data} alt="evidence" className="max-h-40 border border-slate-200 rounded-sm" />
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-between">
              {viewing.public_token && (
                <Button size="sm" variant="outline" onClick={() => window.open(`/public/disposal-tickets/${viewing.public_token}`, '_blank')} data-testid="disposal-view-public">
                  <ExternalLink className="w-3 h-3 mr-1" /> Public View
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => printFullTicket(viewing)} data-testid="disposal-print-ticket"><FileText className="w-3 h-3 mr-1" /> Print Ticket</Button>
              <Button size="sm" variant="outline" onClick={() => printLabel(viewing)} data-testid="disposal-print-label"><Download className="w-3 h-3 mr-1" /> Print Label (4×6)</Button>
              <div className="flex gap-2 ml-auto">
                {viewing.status === 'draft' && <Button size="sm" onClick={() => transition(viewing, 'approved')} className="bg-blue-600 hover:bg-blue-700">Approve</Button>}
                {viewing.status === 'approved' && <Button size="sm" onClick={() => transition(viewing, 'disposed')} className="bg-emerald-600 hover:bg-emerald-700">Mark Disposed</Button>}
                <Button size="sm" variant="outline" onClick={() => setViewing(null)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisposalTicketsPage;
